<template>
  <div class="auth-page" :style="clientColor ? { '--brand-color': clientColor } : null">
    <header class="top-bar">
      <div class="brand-pill" :style="{ backgroundColor: logoBackgroundColor }">
        <img v-if="clientLogo" :src="clientLogo" alt="Client Logo" class="brand-image" />
        <img v-else src="@/../public/static/img/acrew-logo-with-effects-white.png" alt="ACREW Logo" class="brand-image default" />
      </div>
      <div class="top-login">
        <img src="@/../public/static/img/acrew-dash-pattern.png" alt="" class="dash-pattern" />
        <span>Sign Up</span>
      </div>
    </header>

    <div class="content-area">
      <div class="card-wrapper">
        <div class="auth-card">

          <!-- Success state -->
          <div v-if="signUpSuccess">
            <div class="heading">
              <div class="heading-text">
                <h2>You're all set!</h2>
              </div>
            </div>
            <p class="step-description">Your account has been created. You can now sign in.</p>
            <v-btn class="btn-primary full-width-btn" @click="goToLogin()">Go to Login</v-btn>
          </div>

          <!-- Form state -->
          <div v-else>
            <div class="heading">
              <div class="heading-text">
                <h2>Create your account</h2>
              </div>
            </div>
            <p class="step-description">Set a password to complete your account setup.</p>
            <v-form ref="signupForm" class="modern-form" @submit.prevent="submit" lazy-validation>
              <div class="form-row form-row--stack-on-mobile">
                <div class="form-row__col">
                  <v-text-field
                    v-model="form.firstName"
                    label="First Name"
                    disabled
                    dense
                    hide-details="auto"
                    class="line-input custom-filled" />
                </div>
                <div class="form-row__col">
                  <v-text-field
                    v-model="form.lastName"
                    label="Last Name"
                    disabled
                    dense
                    hide-details="auto"
                    class="line-input custom-filled" />
                </div>
              </div>
              <v-text-field
                v-model="form.email"
                label="Email"
                disabled
                dense
                hide-details="auto"
                class="line-input custom-filled" />
              <v-text-field
                v-model="form.password"
                label="Password"
                type="password"
                :rules="[rules.required, rules.pass_all, rules.max50]"
                validate-on-blur
                required
                dense
                hide-details="auto"
                class="line-input custom-filled" />
              <v-text-field
                v-model="form.confirmPassword"
                label="Repeat Password"
                type="password"
                :rules="[rules.required, rules.pass_all, rules.max50, mustMatch]"
                validate-on-blur
                required
                dense
                hide-details="auto"
                class="line-input custom-filled" />
              <div class="button-row">
                <v-btn class="btn-primary" type="submit" :disabled="submitting">Create Account</v-btn>
              </div>
              <p class="terms-note">By creating an account, you agree to receive email and/or SMS messages from the Acrew platform. These messages may include confirmations, reminders, and special offers or promotions from your dental provider.</p>
            </v-form>
          </div>

        </div>
      </div>
    </div>

    <div v-if="clientColor" class="powered-by">
      <span>BROUGHT TO YOU BY</span>
      <img src="@/../public/static/img/acrew-logo-with-effects.png" alt="Logo" />
    </div>
  </div>
</template>

<script>
import DataService from '../../_services/DataService';
import ValidateService from '../../_services/ValidateService';
import { loadClientPreferences } from '../utils/UxUtils';

export default {
  name: 'PatientSignup',
  data() {
    return {
      form: {
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        email: '',
        patientId: '',
      },
      submitting: false,
      signUpSuccess: false,
      rules: ValidateService.validators,
      clientLogo: null,
      clientColor: null,
      whiteBehindLogo: false,
    };
  },
  computed: {
    logoBackgroundColor() {
      return this.whiteBehindLogo ? 'white' : (this.clientColor || '#11405c');
    },
  },
  methods: {
    goToLogin() {
      this.$router.push('/login');
    },
    mustMatch() {
      return this.form.password === this.form.confirmPassword ? true : 'Passwords do not match.';
    },
    async submit() {
      if (!this.$refs.signupForm.validate()) return;
      this.submitting = true;
      const response = await DataService.addPatientUser({
        password: this.form.password,
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        email: this.form.email,
        patientId: this.form.patientId,
      });
      if (response.status === 201) {
        this.signUpSuccess = true;
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: response.data || 'Something went wrong. Please try again.',
        });
      }
      this.submitting = false;
    },
  },
  async mounted() {
    if (this.$route.query.q) {
      const { patientId, clientId, firstName, lastName, email } = JSON.parse(atob(this.$route.query.q));
      DataService.updateClientId(clientId);
      this.form.patientId = patientId;
      this.form.firstName = firstName;
      this.form.lastName = lastName;
      this.form.email = email;
    } else {
      if (this.$route.query.clientId) {
        DataService.updateClientId(this.$route.query.clientId);
      }
      this.form.patientId = this.$route.query.patientId;
      this.form.firstName = this.$route.query.firstName;
      this.form.lastName = this.$route.query.lastName;
      this.form.email = this.$route.query.email;
    }
    await loadClientPreferences(this);
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/auth/page.scss';

.form-row {
  display: flex;
  width: 100%;
  gap: 16px;
  margin-bottom: 0;

  &__col {
    flex: 1 1 0;
    min-width: 0;
  }

  &--stack-on-mobile {
    @media (max-width: 600px) {
      flex-direction: column;
      gap: 0;
    }
  }
}

.full-width-btn {
  width: 100%;
  margin-top: 5px;
}

.terms-note {
  margin-top: 16px;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

p.step-description {
  margin-bottom: 16px;
  color: #555;
}
</style>
